import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Inline-editable table.
 *
 * Props:
 *   columns:        array of { key, label, type, readonly, options, width, render, formatValue }
 *                   - type: 'text' | 'number' | 'bigint' | 'decimal' | 'select' | 'boolean' | 'color'
 *                   - options: for type 'select', array of { value, label }
 *                   - readonly: column not editable
 *                   - render: optional custom cell renderer (value, row) => ReactNode (display only)
 *   rows:           array of objects (each must have a stable id field, configurable via rowKey)
 *   rowKey:         field used as React key (default 'id')
 *   onRowSave:      async (originalRow, patch) => void
 *                   Called when user clicks Save on a row.
 *                   `patch` only contains the fields that changed.
 *   onRowDelete:    optional async (row) => void — if provided, shows a delete button
 *   onAddRow:       optional async () => void  — if provided, shows an "Add row" button
 *                   The handler is responsible for opening any UI it wants.
 *   addRowLabel:    string label for the add button
 *   emptyMessage:   string shown when rows.length === 0
 *   loading:        boolean — show skeleton
 *   getRowError:    optional (row, draft) => string|null — return an error message blocks save
 *
 * Notes:
 *   - Component holds local "draft" state per row, only commits to parent via onRowSave.
 *   - Mobile: collapses to per-row card layout.
 */
const inputBase =
  'w-full px-2 py-1.5 rounded-md bg-card border border-border text-foreground text-sm outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 disabled:opacity-60';

const FieldEditor = ({ column, value, onChange, disabled }) => {
  const v = value ?? '';
  const handle = (raw) => {
    if (column.type === 'number' || column.type === 'decimal') {
      onChange(raw === '' ? null : Number(raw));
    } else if (column.type === 'bigint') {
      onChange(raw === '' ? null : raw);
    } else {
      onChange(raw);
    }
  };

  switch (column.type) {
    case 'boolean':
      return (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(!value)}
          className={`relative w-10 h-6 rounded-full transition-colors ${
            value ? 'bg-accent' : 'bg-muted'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          aria-pressed={!!value}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-card shadow-elevation-1 transition-transform ${
              value ? 'translate-x-4' : ''
            }`}
          />
        </button>
      );
    case 'select':
      return (
        <select
          disabled={disabled}
          value={v}
          onChange={(e) => handle(e.target.value)}
          className={inputBase}
        >
          {(column.options || []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    case 'color':
      return (
        <div className="flex items-center gap-2">
          <input
            type="color"
            disabled={disabled}
            value={v || '#000000'}
            onChange={(e) => handle(e.target.value)}
            className="w-8 h-8 rounded border border-border cursor-pointer"
          />
          <input
            type="text"
            disabled={disabled}
            value={v}
            onChange={(e) => handle(e.target.value)}
            className={`${inputBase} font-data flex-1`}
            placeholder="#000000"
          />
        </div>
      );
    case 'number':
    case 'decimal':
      return (
        <input
          type="number"
          step={column.step || (column.type === 'decimal' ? 'any' : 1)}
          disabled={disabled}
          value={v === null ? '' : v}
          onChange={(e) => handle(e.target.value)}
          className={`${inputBase} tabular-nums`}
        />
      );
    case 'bigint':
      return (
        <input
          type="number"
          step="1"
          disabled={disabled}
          value={v === null ? '' : v}
          onChange={(e) => handle(e.target.value)}
          className={`${inputBase} tabular-nums`}
        />
      );
    case 'text':
    default:
      return (
        <input
          type="text"
          disabled={disabled}
          value={v ?? ''}
          onChange={(e) => handle(e.target.value)}
          className={inputBase}
        />
      );
  }
};

const EditableRow = ({
  row,
  columns,
  rowKey,
  onRowSave,
  onRowDelete,
  getRowError,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(row);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);

  // Reset draft when parent row reference changes (after save/refetch).
  useEffect(() => {
    setDraft(row);
  }, [row]);

  const dirty = columns.some((c) => {
    if (c.readonly) return false;
    return JSON.stringify(draft?.[c.key]) !== JSON.stringify(row?.[c.key]);
  });

  const handleField = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const handleSave = async () => {
    setError(null);
    if (getRowError) {
      const err = getRowError(row, draft);
      if (err) {
        setError(err);
        return;
      }
    }
    const patch = {};
    columns.forEach((c) => {
      if (c.readonly) return;
      if (JSON.stringify(draft[c.key]) !== JSON.stringify(row[c.key])) {
        patch[c.key] = draft[c.key];
      }
    });
    if (Object.keys(patch).length === 0) return;
    setSaving(true);
    try {
      await onRowSave(row, patch);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onRowDelete) return;
    if (!window.confirm(t('superadmin.businessRules.confirmDelete', 'Delete this row?'))) return;
    setDeleting(true);
    setError(null);
    try {
      await onRowDelete(row);
    } catch (e) {
      setError(e?.message || 'Delete failed');
      setDeleting(false);
    }
  };

  const busy = saving || deleting;

  return (
    <>
      {/* Desktop row */}
      <tr className="hidden md:table-row border-b border-border last:border-b-0 hover:bg-surface-2/50 transition-colors">
        {columns.map((c) => (
          <td
            key={c.key}
            className="px-3 py-2 align-middle"
            style={{ width: c.width }}
          >
            {c.readonly ? (
              <span className="text-sm text-foreground">
                {c.render
                  ? c.render(draft[c.key], draft)
                  : c.formatValue
                  ? c.formatValue(draft[c.key])
                  : draft[c.key] ?? '—'}
              </span>
            ) : (
              <FieldEditor
                column={c}
                value={draft[c.key]}
                onChange={(v) => handleField(c.key, v)}
                disabled={busy}
              />
            )}
          </td>
        ))}
        <td className="px-3 py-2 text-right whitespace-nowrap">
          {error && (
            <span className="text-xs text-error mr-2" title={error}>
              ⚠
            </span>
          )}
          {savedFlash && (
            <span className="text-xs text-success mr-2">✓</span>
          )}
          {dirty && (
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="px-3 py-1 text-xs font-semibold rounded-md bg-accent text-white hover:bg-accent/90 disabled:opacity-50 transition-colors mr-2"
            >
              {saving
                ? t('superadmin.businessRules.saving', 'Saving…')
                : t('superadmin.businessRules.save', 'Save')}
            </button>
          )}
          {onRowDelete && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={busy}
              className="px-2 py-1 text-xs rounded-md text-error hover:bg-error/10 disabled:opacity-50 transition-colors"
              aria-label="Delete"
            >
              ✕
            </button>
          )}
        </td>
      </tr>

      {/* Mobile card */}
      <tr className="md:hidden">
        <td colSpan={columns.length + 1} className="p-0">
          <div className="border-b border-border p-4 space-y-2">
            {columns.map((c) => (
              <div key={c.key} className="flex items-start justify-between gap-3">
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold pt-2 shrink-0">
                  {c.label}
                </label>
                <div className="flex-1 max-w-[60%]">
                  {c.readonly ? (
                    <div className="text-sm text-foreground py-1.5">
                      {c.render
                        ? c.render(draft[c.key], draft)
                        : c.formatValue
                        ? c.formatValue(draft[c.key])
                        : draft[c.key] ?? '—'}
                    </div>
                  ) : (
                    <FieldEditor
                      column={c}
                      value={draft[c.key]}
                      onChange={(v) => handleField(c.key, v)}
                      disabled={busy}
                    />
                  )}
                </div>
              </div>
            ))}
            {error && <div className="text-xs text-error mt-1">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              {dirty && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={busy}
                  className="px-3 py-1.5 text-xs font-semibold rounded-md bg-accent text-white hover:bg-accent/90 disabled:opacity-50 transition-colors"
                >
                  {saving
                    ? t('superadmin.businessRules.saving', 'Saving…')
                    : t('superadmin.businessRules.save', 'Save')}
                </button>
              )}
              {onRowDelete && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={busy}
                  className="px-2 py-1.5 text-xs rounded-md text-error hover:bg-error/10 disabled:opacity-50 transition-colors"
                >
                  {t('superadmin.businessRules.delete', 'Delete')}
                </button>
              )}
            </div>
          </div>
        </td>
      </tr>
    </>
  );
};

const EditableTable = ({
  columns,
  rows,
  rowKey = 'id',
  onRowSave,
  onRowDelete,
  onAddRow,
  addRowLabel,
  emptyMessage,
  loading,
  getRowError,
  footer,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-12 rounded-xl skeleton" />
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-card border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="hidden md:table-header-group">
            <tr className="border-b border-border bg-surface-2">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className="text-left px-3 py-3 text-xs uppercase tracking-widest text-muted-foreground font-semibold"
                  style={{ width: c.width }}
                >
                  {c.label}
                </th>
              ))}
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="px-4 py-12 text-center text-muted-foreground text-sm"
                >
                  {emptyMessage ||
                    t('superadmin.businessRules.empty', 'No rows yet.')}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <EditableRow
                  key={r[rowKey]}
                  row={r}
                  columns={columns}
                  rowKey={rowKey}
                  onRowSave={onRowSave}
                  onRowDelete={onRowDelete}
                  getRowError={getRowError}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      {(onAddRow || footer) && (
        <div className="px-4 py-3 border-t border-border bg-surface-2/40 flex items-center justify-between gap-3">
          {footer}
          {onAddRow && (
            <button
              type="button"
              onClick={onAddRow}
              className="ml-auto px-3 py-1.5 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary/90 transition-colors"
            >
              + {addRowLabel || t('superadmin.businessRules.addRow', 'Add row')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default EditableTable;
